import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { toast } from 'react-toastify';

// Error types
export const ERROR_TYPES = {
  NETWORK: 'NETWORK',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  VALIDATION: 'VALIDATION',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  UPLOAD_FAILED: 'UPLOAD_FAILED',
  SYSTEM: 'SYSTEM',
  UNKNOWN: 'UNKNOWN',
};

// Error severity levels
export const ERROR_SEVERITY = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
};

// Initial state
const initialState = {
  errors: [],
  globalError: null,
  isReporting: false,
};

// Action types
const ERROR_ACTIONS = {
  ADD_ERROR: 'ADD_ERROR',
  REMOVE_ERROR: 'REMOVE_ERROR',
  CLEAR_ERRORS: 'CLEAR_ERRORS',
  SET_GLOBAL_ERROR: 'SET_GLOBAL_ERROR',
  CLEAR_GLOBAL_ERROR: 'CLEAR_GLOBAL_ERROR',
  SET_REPORTING: 'SET_REPORTING',
};

// Error reducer
function errorReducer(state, action) {
  switch (action.type) {
    case ERROR_ACTIONS.ADD_ERROR:
      return {
        ...state,
        errors: [...state.errors, action.payload],
      };
    
    case ERROR_ACTIONS.REMOVE_ERROR:
      return {
        ...state,
        errors: state.errors.filter(error => error.id !== action.payload),
      };
    
    case ERROR_ACTIONS.CLEAR_ERRORS:
      return {
        ...state,
        errors: [],
      };
    
    case ERROR_ACTIONS.SET_GLOBAL_ERROR:
      return {
        ...state,
        globalError: action.payload,
      };
    
    case ERROR_ACTIONS.CLEAR_GLOBAL_ERROR:
      return {
        ...state,
        globalError: null,
      };
    
    case ERROR_ACTIONS.SET_REPORTING:
      return {
        ...state,
        isReporting: action.payload,
      };
    
    default:
      return state;
  }
}

// Create context
const ErrorContext = createContext();

// Error provider component
export const ErrorProvider = ({ children }) => {
  const [state, dispatch] = useReducer(errorReducer, initialState);

  // Generate unique error ID
  const generateErrorId = useCallback(() => {
    return `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Classify error type based on error object
  const classifyError = useCallback((error) => {
    if (!error) return ERROR_TYPES.UNKNOWN;

    const message = error.message?.toLowerCase() || '';
    const code = error.code?.toLowerCase() || '';

    if (message.includes('network') || message.includes('fetch') || code.includes('network')) {
      return ERROR_TYPES.NETWORK;
    }
    
    if (message.includes('auth') || code.includes('auth') || error.name === 'AuthError') {
      return ERROR_TYPES.AUTHENTICATION;
    }
    
    if (message.includes('permission') || message.includes('unauthorized') || message.includes('forbidden')) {
      return ERROR_TYPES.AUTHORIZATION;
    }
    
    if (message.includes('validation') || message.includes('invalid') || message.includes('required')) {
      return ERROR_TYPES.VALIDATION;
    }
    
    if (message.includes('not found') || code.includes('not-found')) {
      return ERROR_TYPES.RESOURCE_NOT_FOUND;
    }
    
    if (message.includes('upload') || message.includes('storage')) {
      return ERROR_TYPES.UPLOAD_FAILED;
    }

    return ERROR_TYPES.SYSTEM;
  }, []);

  // Determine error severity
  const determineSeverity = useCallback((error, type) => {
    if (type === ERROR_TYPES.AUTHENTICATION || type === ERROR_TYPES.SYSTEM) {
      return ERROR_SEVERITY.HIGH;
    }
    
    if (type === ERROR_TYPES.AUTHORIZATION || type === ERROR_TYPES.UPLOAD_FAILED) {
      return ERROR_SEVERITY.MEDIUM;
    }
    
    return ERROR_SEVERITY.LOW;
  }, []);

  // Get user-friendly error message
  const getErrorMessage = useCallback((error, type) => {
    switch (type) {
      case ERROR_TYPES.NETWORK:
        return 'Please check your internet connection and try again.';
      case ERROR_TYPES.AUTHENTICATION:
        return 'Please log in again to continue.';
      case ERROR_TYPES.AUTHORIZATION:
        return 'You don\'t have permission to perform this action.';
      case ERROR_TYPES.VALIDATION:
        return 'Please check your input and try again.';
      case ERROR_TYPES.RESOURCE_NOT_FOUND:
        return 'The requested resource was not found.';
      case ERROR_TYPES.UPLOAD_FAILED:
        return 'Failed to upload file. Please try again.';
      default:
        return error.message || 'Something went wrong. Please try again.';
    }
  }, []);

  // Show error toast with appropriate styling
  const showErrorToast = useCallback((message, severity) => {
    const options = {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    };

    switch (severity) {
      case ERROR_SEVERITY.CRITICAL:
      case ERROR_SEVERITY.HIGH:
        toast.error(message, { ...options, autoClose: 8000 });
        break;
      case ERROR_SEVERITY.MEDIUM:
        toast.warn(message, options);
        break;
      default:
        toast.info(message, { ...options, autoClose: 3000 });
        break;
    }
  }, []);

  // Report error to external service
  const reportErrorToService = useCallback(async (errorInfo) => {
    try {
      dispatch({ type: ERROR_ACTIONS.SET_REPORTING, payload: true });

      // In a real app, send to your error tracking service
      const errorReport = {
        id: errorInfo.id,
        message: errorInfo.message,
        stack: errorInfo.stack,
        type: errorInfo.type,
        severity: errorInfo.severity,
        timestamp: errorInfo.timestamp,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: errorInfo.userId || 'anonymous',
        buildVersion: process.env.REACT_APP_VERSION || 'unknown',
        context: errorInfo.context,
      };

      // Mock API call - replace with your actual error reporting service
      console.error('Error Report:', errorReport);
      
      // Example: Send to your API
      // await fetch('/api/errors', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(errorReport)
      // });

    } catch (reportingError) {
      console.error('Failed to report error:', reportingError);
    } finally {
      dispatch({ type: ERROR_ACTIONS.SET_REPORTING, payload: false });
    }
  }, []);

  // Add error to state and handle it
  const addError = useCallback((error, context = {}) => {
    const errorId = generateErrorId();
    const type = classifyError(error);
    const severity = determineSeverity(error, type);
    
    const errorInfo = {
      id: errorId,
      message: error.message || 'An unknown error occurred',
      stack: error.stack,
      type,
      severity,
      timestamp: new Date().toISOString(),
      context,
      userId: context.userId,
    };

    dispatch({ type: ERROR_ACTIONS.ADD_ERROR, payload: errorInfo });

    // Show appropriate toast notification
    const toastMessage = getErrorMessage(error, type);
    showErrorToast(toastMessage, severity);

    // Report high severity errors automatically
    if (severity === ERROR_SEVERITY.HIGH || severity === ERROR_SEVERITY.CRITICAL) {
      reportErrorToService(errorInfo);
    }

    return errorId;
  }, [generateErrorId, classifyError, determineSeverity, reportErrorToService, getErrorMessage, showErrorToast]);

  // Remove error from state
  const removeError = useCallback((errorId) => {
    dispatch({ type: ERROR_ACTIONS.REMOVE_ERROR, payload: errorId });
  }, []);

  // Clear all errors
  const clearErrors = useCallback(() => {
    dispatch({ type: ERROR_ACTIONS.CLEAR_ERRORS });
  }, []);

  // Set global error (for critical errors that affect the entire app)
  const setGlobalError = useCallback((error, context = {}) => {
    const errorId = generateErrorId();
    const type = classifyError(error);
    
    const errorInfo = {
      id: errorId,
      message: error.message || 'A critical error occurred',
      stack: error.stack,
      type,
      severity: ERROR_SEVERITY.CRITICAL,
      timestamp: new Date().toISOString(),
      context,
    };

    dispatch({ type: ERROR_ACTIONS.SET_GLOBAL_ERROR, payload: errorInfo });
    reportErrorToService(errorInfo);
  }, [generateErrorId, classifyError, reportErrorToService]);

  // Clear global error
  const clearGlobalError = useCallback(() => {
    dispatch({ type: ERROR_ACTIONS.CLEAR_GLOBAL_ERROR });
  }, []);

  // Handle async operations with error catching
  const handleAsync = useCallback(async (asyncFn, context = {}) => {
    try {
      return await asyncFn();
    } catch (error) {
      addError(error, context);
      throw error; // Re-throw so calling code can handle it if needed
    }
  }, [addError]);

  // Context value
  const value = {
    // State
    errors: state.errors,
    globalError: state.globalError,
    isReporting: state.isReporting,
    
    // Actions
    addError,
    removeError,
    clearErrors,
    setGlobalError,
    clearGlobalError,
    handleAsync,
    reportErrorToService,
    
    // Utils
    ERROR_TYPES,
    ERROR_SEVERITY,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook to use error context
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};

// HOC for automatic error handling
export const withErrorHandler = (Component) => {
  const WrappedComponent = (props) => {
    const { addError } = useError();
    
    const errorHandler = useCallback((error, context = {}) => {
      addError(error, { ...context, component: Component.name });
    }, [addError]);

    return <Component {...props} onError={errorHandler} />;
  };
  
  WrappedComponent.displayName = `withErrorHandler(${Component.displayName || Component.name})`;
  return WrappedComponent;
};